// app/components/thailand-premium-tapioca-starch-products/IndustrialGrade.tsx
"use client";

import * as React from "react";
import { useEffect, useState } from "react";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import { Box, Grid } from "@mui/material";
import { useTranslations, useLocale } from "next-intl";
import Image from "next/image";

type InlineEdit = {
  id: string;
  type: "text" | "image";
  selector: string;
  value: string;
  cmsKey?: string | null;
};

export default function IndustrialGrade() {
  const t = useTranslations("OurProducts");
  const locale = useLocale();

  // path ของหน้า (ให้ตรงกับที่เซฟใน page-inline)
  const [pagePath, setPagePath] = useState<string>("/thailand-premium-tapioca-starch-products");
  useEffect(() => {
    if (typeof window === "undefined") return;
    setPagePath(window.location.pathname);
  }, []);

  // ---- default text/image จาก i18n + static asset (ดีต่อ SEO / SSR) ----
  const defaultTitle = t("IndustrialGrade.title");
  const defaultSubtitle = t("IndustrialGrade.subtitle");

  const defaultItem1 = t("IndustrialGrade.List1.item1");
  const defaultItem2 = t("IndustrialGrade.List1.item2");
  const defaultItem3 = t("IndustrialGrade.List1.item3");
  const defaultItem4 = t("IndustrialGrade.List1.item4");

  const defaultImage = "/assets/thailand-premium-tapioca-starch-products/industrialgrade/industrial.png";

  // ---- state สำหรับใช้แสดงจริง (จะโดน inline override ถ้ามี) ----
  const [title, setTitle] = useState(defaultTitle);
  const [subtitle, setSubtitle] = useState(defaultSubtitle);
  const [item1, setItem1] = useState(defaultItem1);
  const [item2, setItem2] = useState(defaultItem2);
  const [item3, setItem3] = useState(defaultItem3);
  const [item4, setItem4] = useState(defaultItem4);
  const [imageSrc, setImageSrc] = useState(defaultImage);

  // ---- locale เปลี่ยน → reset กลับค่า default ก่อน ----
  useEffect(() => {
    setTitle(defaultTitle);
    setSubtitle(defaultSubtitle);
    setItem1(defaultItem1);
    setItem2(defaultItem2);
    setItem3(defaultItem3);
    setItem4(defaultItem4);
    setImageSrc(defaultImage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

 //
  useEffect(() => {
    if (!pagePath) return;

    const fetchInline = async () => {
      try {
        const res = await fetch(
          `/api/admin/page-inline?websiteId=${process.env.NEXT_PUBLIC_WEBSITE_ID || process.env.WEBSITE_ID}&path=${encodeURIComponent(
            pagePath
          )}&locale=${locale}`,
          {
            credentials: "include",
            cache: "no-store",
          }
        );

        if (!res.ok) return;

        const data = await res.json();
        if (!data?.edits) return;

        const list: InlineEdit[] = data.edits;
        const map: Record<string, string> = {};

        list.forEach((e) => {
          let key = e.cmsKey ?? undefined;

          // fallback: ดึงจาก selector ถ้าใน DB ยังไม่มี cmsKey
          if (!key && e.selector) {
            const match = e.selector.match(/data-cms-key="([^"]+)"/);
            if (match) key = match[1];
          }

          if (key) {
            map[key] = e.value;
          }
        });

        // ---- map key → state ----
        if (map["ourproducts.industrial.title"])
          setTitle(map["ourproducts.industrial.title"]);
        if (map["ourproducts.industrial.subtitle"])
          setSubtitle(map["ourproducts.industrial.subtitle"]);

        if (map["ourproducts.industrial.list1.item1"])
          setItem1(map["ourproducts.industrial.list1.item1"]);
        if (map["ourproducts.industrial.list1.item2"])
          setItem2(map["ourproducts.industrial.list1.item2"]);
        if (map["ourproducts.industrial.list1.item3"])
          setItem3(map["ourproducts.industrial.list1.item3"]);
        if (map["ourproducts.industrial.list1.item4"])
          setItem4(map["ourproducts.industrial.list1.item4"]);

        if (map["ourproducts.industrial.image"])
          setImageSrc(map["ourproducts.industrial.image"]);
      } catch (err) {
        console.warn("[IndustrialGrade] inline fetch error:", err);
      }
    };

    fetchInline();
  }, [locale, pagePath]);

  return (
    <Box id="industrial-grade" className="industrialgrade-wrap">
      <Container className="container" sx={{ px: { xs: 0, md: 3 } }}>
        <Box
          className="industrialgrade-content"
          sx={{
            px: { xs: 3, md: 10 },
            py: { xs: 5, md: 15 }
          }}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }} data-aos="fade-up">
              <Typography
                variant="h2"
                data-cms-key="ourproducts.industrial.title"
                sx={{
                  mb: 2,
                  whiteSpace: { xs: "normal", lg: "pre-line" }
                }}>
                {title}
              </Typography>
              <Typography
                variant="h3"
                sx={{ whiteSpace: { xs: "normal", lg: "pre-line" } }}
                data-cms-key="ourproducts.industrial.subtitle"
              >
                {subtitle}
              </Typography>
              <ul>
                <li data-cms-key="ourproducts.industrial.list1.item1">
                  {item1}
                </li>
                <li data-cms-key="ourproducts.industrial.list1.item2">
                  {item2}
                </li>
                <li data-cms-key="ourproducts.industrial.list1.item3">
                  {item3}
                </li>
                <li data-cms-key="ourproducts.industrial.list1.item4">
                  {item4}
                </li>
              </ul>
            </Grid>
            <Grid
              size={{ xs: 12, md: 6 }}
              data-aos="zoom-in"
              sx={{
                position: "relative"
              }}
            >
              <Box className="industrialgrade-img">
                <Image
                  src={imageSrc}
                  title="thailand industrial grade tapioca"
                  alt="Industrial grade tapioca starch for paper, adhesive and textile manufacturing"
                  width={693}
                  height={616}
                  className="img-fluid"
                  data-cms-key="ourproducts.industrial.image"
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
}